import { zodResolver } from '@hookform/resolvers/zod'
import { AwardType, TenantMemberAndTeamDisplayData } from '@kudos/http-client'
import { MentionsTextAreaFieldSchema } from '@shared/forms/mentionsTextareaField/MentionsTextareaField'
import stripHtml from '@shared/utils/stripHtml'
import { t } from '@transifex/native'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import useSendAwardMutations from './useSendAwardMutations'

const SendAwardFormSchema = z.object({
  amount: z.preprocess(
    value => (typeof value === 'string' ? parseInt(value, 10) : value),
    z
      .number({
        message: t('Points required'),
      })
      .min(0, { message: t('Points must be a positive number') }),
  ),
  message: MentionsTextAreaFieldSchema(),
  sendTo: z.array(z.custom<TenantMemberAndTeamDisplayData>()).min(1, t('Select a recipient')),
})

export type SendAwardFormData = z.infer<typeof SendAwardFormSchema>

const useSendAwardForm = (awardType: AwardType) => {
  const { id: awardTypeId, points } = awardType

  const defaults: SendAwardFormData = {
    amount: points,
    message: '',
    sendTo: [],
  }

  const formMethods = useForm<SendAwardFormData>({
    resolver: zodResolver(SendAwardFormSchema),
    defaultValues: defaults,
  })

  const { reset, setError, formState, control } = formMethods
  const { publishAward } = useSendAwardMutations(awardTypeId, setError)
  const sendTo = useWatch({ control, name: 'sendTo' })
  const message = useWatch({ control, name: 'message' })
  const isFormDirty = formState.isDirty && (sendTo.length > 0 || stripHtml(message).length > 0)

  const handleResetAwardForm = () => {
    reset(defaults)
  }

  return {
    formMethods,
    publishAward,
    handleResetAwardForm,
    isFormDirty,
  }
}

export default useSendAwardForm
